/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from "react";
import { sortCbq } from "../utils/cbqOrder";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { GetCBQSession } from "../api/Quiz/GetCBQSession";
import { SaveCBQResponse, CBQResponseBody } from "../api/Quiz/SaveCBQResponse";
import { SubmitCBQSession } from "../api/Quiz/SubmitCBQSession";
import {
  CBQSavedResponse,
  CBQStudentQuestion,
  CBQStudentScenario,
} from "../api/Quiz/cbqStudentTypes";
import Spinner from "../components/Shared/Spinner";
import CbqQuestion from "../components/CbqExam/CbqQuestion";
import QuizClockIcon from "../components/Icons/QuizClockIcon";
import FlagIcon from "../components/Icons/FlagIcon";
import QuizArrowIcon from "../components/Icons/QuizArrowIcon";
import PdfQuizIcon from "../components/Icons/PdfQuizIcon";
import QuizFolderIcon from "../components/Icons/QuizFolderIcon";
import { useUserDataStore } from "../stores/user";
import Draggable from "../components/common/Draggable";
import HiddenPdfLoader from "../components/common/HiddenPdfLoader";
import { isNativeMode, withNativeParam } from "../lib/nativeMode";
import CloseIcon from "../components/Icons/CloseIcon";
import Calculator from "../components/Quiz/Calculator/Calculator";
import CalculatorIcon from "../components/Icons/CalculatorIcon";
import Logo from "../assets/NWWhiteLogo.png";

type Item =
  | { kind: "scenario"; scenario: CBQStudentScenario; scenarioIndex: number }
  | {
      kind: "question";
      q: CBQStudentQuestion;
      scenario: CBQStudentScenario;
      scenarioIndex: number;
      qNumber: number;
    };

// Ordering lives in the shared comparator (sortKey first, then `order`).
const bodyHasAnswer = (b: CBQResponseBody) =>
  !!(
    b.mcq ||
    b.multiResponse ||
    b.dropdown ||
    b.dragDropOrdering ||
    b.dragDropClassification ||
    b.numeric
  );

const formatTime = (t: number) => {
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(
    s
  ).padStart(2, "0")}`;
};

const CbqSession = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useUserDataStore();
  const id = sessionId || "";

  const backTo = (location.state as { from?: string } | null)?.from;

  const [current, setCurrent] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [answered, setAnswered] = useState<Set<string>>(new Set());
  const [flagged, setFlagged] = useState<Set<string>>(new Set());
  const [viewedScenarios, setViewedScenarios] = useState<Set<string>>(
    new Set()
  );

  const [pdfWindowOpen, setPdfWindowOpen] = useState(false);
  const [pdfWindowUrl, setPdfWindowUrl] = useState("");
  const [calcOpen, setCalcOpen] = useState(false);
  const [pdfNonce, setPdfNonce] = useState(0);
  const nativeMode = isNativeMode();
  const openPdfWindow = (url?: string) => {
    if (!url) return;
    setPdfWindowUrl(url);
    setPdfWindowOpen(true);
    // Every tap must behave exactly like the first one. Bumping the nonce
    // remounts the loader, so the iframe re-requests the PDF and the host is
    // messaged again even when the URL is identical — without it, re-tapping
    // the same PDF changes no state and nothing happens.
    setPdfNonce((n) => n + 1);
  };

  // Tool buttons shown under a scenario/question. Calculator only under questions.
  const toolButtons = (pdfUrl?: string, withCalc = true) => (
    <div className="flex flex-col items-start gap-2">
      {pdfUrl && (
        <button
          type="button"
          onClick={() => openPdfWindow(pdfUrl)}
          className="p-2 bg-lightGray/10 rounded-lg gap-2 flex items-center sm:text-base text-xs justify-center"
        >
          <PdfQuizIcon />
          View Scenario PDF
          <QuizFolderIcon />
        </button>
      )}
      {withCalc && (
        <>
          <button
            type="button"
            onClick={() => setCalcOpen((v) => !v)}
            className="p-2 bg-lightGray/10 rounded-lg gap-2 flex items-center sm:text-base text-xs justify-center"
          >
            <CalculatorIcon />
            Calculator
          </button>
          {/* Under `?isMobile` the calculator is a plain inline block sitting
              above the answer options rather than a floating popup — dragging a
              window is unusable in the host webview. Same button toggles it. */}
          {nativeMode && calcOpen && (
            <div className="w-full">
              <Calculator />
            </div>
          )}
        </>
      )}
    </div>
  );

  const { data, isLoading } = useQuery({
    queryFn: () => GetCBQSession(id),
    queryKey: ["cbq-session", id],
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const payload = data?.data;

  // Scenario info pages + their questions, in one navigable sequence.
  const items: Item[] = useMemo(() => {
    const scenarios = sortCbq(payload?.scenarios ?? []);
    const out: Item[] = [];
    let qn = 0;
    scenarios.forEach((scenario, scenarioIndex) => {
      out.push({ kind: "scenario", scenario, scenarioIndex });
      sortCbq(scenario.questions ?? [])
        .forEach((q) => {
          qn += 1;
          out.push({ kind: "question", q, scenario, scenarioIndex, qNumber: qn });
        });
    });
    return out;
  }, [payload]);

  const totalQuestions = items.filter((it) => it.kind === "question").length;

  const savedByQuestion = useMemo(() => {
    const map: Record<string, CBQSavedResponse> = {};
    (payload?.responses ?? []).forEach((r) => {
      if (r?.cbqScenarioQuestionId) map[r.cbqScenarioQuestionId] = r;
    });
    return map;
  }, [payload]);

  useEffect(() => {
    const ans = new Set<string>();
    const flg = new Set<string>();
    (payload?.responses ?? []).forEach((r) => {
      const qid = r?.cbqScenarioQuestionId;
      if (!qid) return;
      if (r.isFlagged) flg.add(qid);
      if (
        r.mcq ||
        r.multiResponse ||
        r.dropdown ||
        r.dragDropOrdering ||
        r.dragDropClassification ||
        r.numeric
      )
        ans.add(qid);
    });
    setAnswered(ans);
    setFlagged(flg);
  }, [payload]);

  // scenarioId -> when the student started that scenario.
  const startedMap = useMemo(() => {
    const m: Record<string, string> = {};
    (payload?.scenarioProgress ?? []).forEach((p) => {
      if (p.scenarioId && p.startedAt) m[p.scenarioId] = p.startedAt;
    });
    return m;
  }, [payload]);

  // Mark a scenario page as viewed once it's opened.
  useEffect(() => {
    const it = items[current];
    if (it?.kind === "scenario") {
      setViewedScenarios((prev) =>
        prev.has(it.scenario.id) ? prev : new Set(prev).add(it.scenario.id)
      );
    }
  }, [current, items]);

  // Per-scenario countdown for the active item's scenario.
  const [remaining, setRemaining] = useState<number | null>(null);
  useEffect(() => {
    const it = items[current];
    const scenario = it?.scenario;
    const startedAt =
      (scenario && startedMap[scenario.id]) ??
      payload?.session?.section2StartedAt;
    const estMin = scenario?.estimatedMinutes;
    if (!startedAt || !estMin) {
      setRemaining(null);
      return;
    }
    const deadline = new Date(startedAt).getTime() + estMin * 60000;
    const tick = () =>
      setRemaining(Math.max(0, Math.floor((deadline - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [items, current, startedMap, payload?.session?.section2StartedAt]);

  const handleSave = async (questionId: string, body: CBQResponseBody) => {
    if (bodyHasAnswer(body)) {
      setAnswered((prev) => new Set(prev).add(questionId));
    }
    const res = await SaveCBQResponse(id, questionId, body);
    if (res.error) toast.error(res.error.message || "Could not save answer");
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const res = await SubmitCBQSession(id);
    setSubmitting(false);
    if (res.error) {
      toast.error(res.error.message || "Could not submit exam");
      return;
    }
    toast.success("Exam submitted");
    navigate(withNativeParam(`/cbq-exam/${id}/result`), {
      state: { from: backTo },
    });
  };

  const submitted = !!payload?.session?.submittedAt;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#dfe7eb]">
        <Spinner />
      </div>
    );
  }

  if (!payload || totalQuestions === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#dfe7eb]">
        <p className="text-[#4d4c4d] font-medium">
          This CBQ exam has no questions yet.
        </p>
      </div>
    );
  }

  const activeItem = items[current];
  const progress = Math.round((answered.size / totalQuestions) * 100);

  const toggleFlag = () => {
    if (activeItem.kind !== "question") return;
    const qid = activeItem.q.id;
    const next = new Set(flagged);
    const willFlag = !next.has(qid);
    if (willFlag) next.add(qid);
    else next.delete(qid);
    setFlagged(next);
    SaveCBQResponse(id, qid, { type: activeItem.q.type, isFlagged: willFlag });
  };

  return (
    <div>
      <div className="mx-auto max-w-5xl lg:w-[64rem] flex flex-col">
        {/* Top bar */}
        <div className="bg-[#4d4c4d] sm:h-16 h-12 px-3 flex flex-col justify-center text-white">
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <Link to="/">
                <img className="sm:size-12 size-8" src={Logo} alt="Logo" />
              </Link>
              <h1 className="md:block hidden">
                {activeItem.kind === "scenario"
                  ? `Scenario ${activeItem.scenarioIndex + 1}`
                  : `Question: ${activeItem.qNumber}`}
              </h1>
            </div>
            <div className="flex sm:gap-8 gap-4 items-center">
              {remaining !== null && (
                <div className="flex items-center gap-2">
                  <div className="sm:block hidden">
                    <QuizClockIcon height={40} width={40} />
                  </div>
                  <div>
                    <h1 className="md:block hidden">Scenario Time Remaining</h1>
                    <p>{formatTime(remaining)}</p>
                  </div>
                </div>
              )}
              <div className="sm:w-32 w-24 text-center">
                <div className="overflow-hidden bg-blue-50 sm:h-3 h-2 rounded-full w-full mb-1">
                  <span
                    className="h-full bg-[#8cc63f] block rounded-full"
                    style={{ width: `${progress}%` }}
                  ></span>
                </div>
                <h1 className="sm:text-base text-xs">Progress {progress}%</h1>
              </div>
              <button
                className="sm:h-10 md:w-36 sm:w-[100px] h-8 w-20 text-sm bg-[#fff8dc] text-black rounded disabled:opacity-60"
                onClick={handleSubmit}
                disabled={submitting || submitted}
              >
                {submitting ? "Submitting..." : "Finish Test"}
              </button>
            </div>
          </div>
        </div>

        {/* Identity bar */}
        <div className="h-6 bg-[#8cc63f] flex justify-between items-center px-3 text-white sm:text-base text-xs">
          <p>Test: CBQ Exam</p>
          <p className="capitalize">
            Candidate: {userData?.firstName} {userData?.lastName}
          </p>
        </div>

        {/* Body */}
        <div className="bg-white p-1 flex sm:flex-row flex-col h-[85vh]">
          {/* Navigator */}
          <div className="sm:min-w-[71px] items-center flex sm:flex-col gap-1 text-white w-full sm:overflow-y-scroll sm:pr-2 pb-2 sm:overflow-x-hidden overflow-x-scroll overflow-y-hidden sm:h-auto min-h-12 sm:w-auto">
            {items.map((it, i) => {
              const isCurrent = i === current;
              if (it.kind === "scenario") {
                const viewed = viewedScenarios.has(it.scenario.id);
                const bg = isCurrent
                  ? "bg-[#35540d]"
                  : viewed
                  ? "bg-[#4d4c4d]"
                  : "bg-[#8cc63f]";
                return (
                  <button
                    key={`s-${it.scenario.id}`}
                    onClick={() => setCurrent(i)}
                    title={`Scenario ${it.scenarioIndex + 1}`}
                    className={`relative min-w-11 min-h-6 rounded flex justify-center items-center overflow-hidden ${bg}`}
                  >
                    {viewed && (
                      <span className="absolute top-0 left-0 w-0 h-0 border-t-[10px] border-r-[10px] border-t-white border-r-transparent" />
                    )}
                    <span
                      className={`flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold leading-none ${
                        viewed
                          ? "bg-white text-[#4d4c4d]"
                          : "border border-white text-white"
                      }`}
                    >
                      i
                    </span>
                  </button>
                );
              }
              const qBg = isCurrent
                ? "bg-[#35540d]"
                : answered.has(it.q.id)
                ? "bg-[#4d4c4d]"
                : "bg-[#8cc63f]";
              return (
                <button
                  key={`q-${it.q.id}`}
                  onClick={() => setCurrent(i)}
                  className={`min-w-11 min-h-6 rounded flex justify-center items-center text-xs relative overflow-hidden ${qBg}`}
                >
                  {answered.has(it.q.id) && (
                    <span className="absolute top-0 left-0 w-0 h-0 border-t-[10px] border-r-[10px] border-t-white border-r-transparent" />
                  )}
                  {it.qNumber}
                  {flagged.has(it.q.id) && (
                    <FlagIcon className="absolute right-1" width={14} height={10} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Main panel */}
          <div className="sm:p-6 p-3 border border-[#9EA7AC] rounded-md w-full h-full overflow-y-scroll">
            {/* Scenario info page (only when current) */}
            {activeItem.kind === "scenario" && (
              <div>
                <p className="bg-[#F5F7F8] p-2.5 mb-3 sm:text-base text-sm text-[#333]">
                  Scenario {activeItem.scenarioIndex + 1}:{" "}
                  {activeItem.scenario.title}
                </p>
                {activeItem.scenario.description ? (
                  <p className="bg-[#F5F7F8] p-2.5 mb-3 sm:text-base text-sm text-[#333] whitespace-pre-line">
                    {activeItem.scenario.description}
                  </p>
                ) : null}
                {toolButtons(activeItem.scenario.pdfUrl, false)}
              </div>
            )}

            {/* Questions kept mounted so answers persist on navigation */}
            {items.map((it, i) =>
              it.kind === "question" ? (
                <div
                  key={`qp-${it.q.id}`}
                  className={i === current ? "" : "hidden"}
                >
                  <CbqQuestion
                    question={it.q}
                    saved={savedByQuestion[it.q.id]}
                    disabled={submitted}
                    onSave={(body) => handleSave(it.q.id, body)}
                    belowPrompt={toolButtons(it.scenario.pdfUrl)}
                  />
                </div>
              ) : null
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="bg-[#4d4c4d] h-14 px-3 flex items-center justify-end gap-2 text-white">
          {activeItem.kind === "question" && (
            <button
              onClick={toggleFlag}
              className={`h-10 w-10 rounded flex items-center justify-center ${
                flagged.has(activeItem.q.id) ? "bg-[#eab326]" : "bg-[#8cc63f]"
              }`}
              title="Flag for review"
            >
              <FlagIcon />
            </button>
          )}
          <button
            className="h-10 w-20 rounded bg-[#8cc63f] flex items-center gap-2 justify-center disabled:opacity-50"
            onClick={() => setCurrent((i) => Math.max(0, i - 1))}
            disabled={current === 0}
          >
            <QuizArrowIcon className="rotate-180" />
            Back
          </button>
          {current < items.length - 1 ? (
            <button
              className="h-10 w-20 rounded bg-[#8cc63f] flex items-center gap-2 justify-center"
              onClick={() => setCurrent((i) => Math.min(items.length - 1, i + 1))}
            >
              Next
              <QuizArrowIcon />
            </button>
          ) : (
            <button
              className="h-10 w-28 rounded bg-[#fff8dc] text-black flex items-center justify-center disabled:opacity-60"
              onClick={handleSubmit}
              disabled={submitting || submitted}
            >
              {submitting ? "..." : "Finish Test"}
            </button>
          )}
        </div>
      </div>

      {pdfWindowOpen && nativeMode && (
        <HiddenPdfLoader
          key={pdfNonce}
          src={pdfWindowUrl}
          title="Scenario PDF"
        />
      )}

      {pdfWindowOpen && !nativeMode && (
        <Draggable handle=".cbq-pdf-header">
          <div
            className="fixed top-24 left-8 z-[9999] flex flex-col bg-white border border-black/20 rounded-lg shadow-2xl overflow-hidden"
            style={{ width: 680, height: 560, resize: "both" }}
          >
            <div className="cbq-pdf-header cursor-move bg-[#4d4c4d] text-white px-3 py-2 flex items-center justify-between select-none">
              <span className="text-sm font-semibold">Scenario PDF</span>
              <button
                type="button"
                onClick={() => setPdfWindowOpen(false)}
                title="Close"
              >
                <CloseIcon fill="#fff" />
              </button>
            </div>
            <iframe
              src={pdfWindowUrl}
              title="Scenario PDF"
              className="flex-1 w-full border-0"
            />
          </div>
        </Draggable>
      )}

      {calcOpen && !nativeMode && (
        <Draggable handle=".cbq-calc-header" cancel=".cbq-calc-close">
          <div className="fixed top-32 left-16 z-[9999] bg-white border border-black/20 rounded-lg shadow-2xl overflow-hidden">
            <div className="cbq-calc-header cursor-move bg-[#4d4c4d] text-white px-3 py-2 flex items-center justify-between select-none">
              <span className="text-sm font-semibold">Calculator</span>
              {/* cbq-calc-close is in Draggable's `cancel` selector: the header
                  is the drag handle, and react-draggable preventDefault()s
                  touchstart inside it, so without this the tap never becomes a
                  click and the button is dead on mobile. */}
              <button
                type="button"
                className="cbq-calc-close"
                onClick={() => setCalcOpen(false)}
                title="Close"
              >
                <CloseIcon fill="#fff" />
              </button>
            </div>
            <Calculator />
          </div>
        </Draggable>
      )}
    </div>
  );
};

export default CbqSession;
